import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, ChevronUp, CheckCircle, Code, List, Edit3, Eye, RotateCcw } from "lucide-react";
import ReactMarkdown from 'react-markdown';
import "../styles/howTos.css";

import { howToData } from "../data/howToData";

// Default data for missing categories
const defaultQa = {
  title: "QA Interview Questions",
  qa: [
    { q: "What is exploratory testing?", a: "An informal testing approach where testers explore the application freely to discover bugs, relying on their experience and intuition." },
    { q: "What is a test case?", a: "A set of conditions, inputs, and expected outcomes designed to verify whether a specific feature works correctly." }
  ]
};

export default function HowToDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const data = howToData[id] || defaultQa;
  const totalLessons = data.qa.length;
  const isProgramCourse = id && id.includes("programs");
  
  const [activeIndex, setActiveIndex] = useState(0);
  const [activeTab, setActiveTab] = useState("learn");
  
  // Practice state
  const [practiceText, setPracticeText] = useState("");
  const [showAnswer, setShowAnswer] = useState(false);

  // Sandbox state
  const getDefaultLanguage = () => {
    const lowerId = id.toLowerCase();
    if (lowerId.includes("python")) return "python";
    if (lowerId.includes("javascript") || lowerId.includes("playwright")) return "javascript";
    if (lowerId.includes("java") || lowerId.includes("selenium") || lowerId.includes("appium")) return "java";
    if (lowerId.includes("sql")) return "sql";
    if (lowerId.includes("c") && !lowerId.includes("course")) return "c";
    return "javascript";
  };

  const [selectedLanguage, setSelectedLanguage] = useState(getDefaultLanguage());
  const [sandboxCode, setSandboxCode] = useState("");
  const [sandboxOutput, setSandboxOutput] = useState("");
  const [isExecuting, setIsExecuting] = useState(false);

  const activeLesson = data.qa[activeIndex] || data.qa[0];

  // Group lessons by category
  const groupedLessons = data.qa.reduce((acc, item, idx) => {
    const category = item.category || "Miscellaneous";
    if (!acc[category]) acc[category] = [];
    acc[category].push({ ...item, originalIndex: idx });
    return acc;
  }, {});
  
  // Force "All Examples" to be the very first category and contain all lessons
  const finalGroupedLessons = {
    "All Examples": data.qa.map((item, idx) => ({ ...item, originalIndex: idx })),
    ...groupedLessons
  };
  
  const categories = Object.keys(finalGroupedLessons);
  
  // Track which category is currently open (null = all closed)
  const [openCategory, setOpenCategory] = useState(null);

  const toggleCategory = (category) => {
    setOpenCategory(prev => prev === category ? null : category);
  };

  // Reset states when lesson changes
  useEffect(() => {
    setPracticeText("");
    setShowAnswer(false);
    
    // Default sandbox code to the example code if it exists
    if (activeLesson && activeLesson.example) {
      setSandboxCode(activeLesson.example.code);
    } else {
      setSandboxCode("");
    }
    setSandboxOutput("");
  }, [activeIndex, activeLesson]);

  const executeCode = async () => {
    setIsExecuting(true);
    setSandboxOutput("Executing...");

    try {
      const response = await fetch('http://localhost:5005/api/execute', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ language: selectedLanguage, code: sandboxCode })
      });
      const result = await response.json();
      setSandboxOutput(result.output || result.error || "Unknown execution error");
    } catch (error) {
      setSandboxOutput("Failed to connect to execution server: " + error.message);
    } finally {
      setIsExecuting(false);
    }
  };

  // Example progress calculation - currently static but can be wired to actual completion
  const completedLessons = 3; 
  const progressPercent = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;

  return (
    <div className="course-layout">
      {/* SIDEBAR */}
      <div className="course-sidebar">
        <button className="back-btn" onClick={() => navigate("/how-tos")}>
          <ChevronLeft size={18} /> Back to How-To's
        </button>

        <div className="course-sidebar-header">
          <h2>{data.title}</h2>
          <span className="lessons-count">{totalLessons} Lessons</span>
        </div>

        <div className="course-progress">
          <div className="progress-text-row">
            <span>Progress</span>
            <strong>{completedLessons}/{totalLessons} lessons</strong>
          </div>
          <div className="progress-bar-container">
            <div className="progress-bar-fill" style={{ width: `${progressPercent}%` }}></div>
          </div>
          <div className="progress-percent-text">{progressPercent}% complete</div>
        </div>

        <div className="course-section-label">
          <span className="section-badge">Basic</span>
          <span>{totalLessons} lessons</span>
        </div>

        <div className="sidebar-lessons-list" style={{ padding: '0' }}>
          {categories.map((category) => (
            <div key={category} className="sidebar-category">
              {isProgramCourse && (
                <div 
                  className={`category-header ${openCategory === category ? 'open' : ''}`}
                  onClick={() => toggleCategory(category)}
                  style={{
                    padding: '16px',
                    fontWeight: '600',
                    color: '#1e293b',
                    cursor: 'pointer',
                    borderBottom: '1px solid #e2e8f0',
                    background: '#ffffff',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <span>{category}</span>
                  {openCategory === category ? (
                    <ChevronUp size={20} color="#94a3b8" />
                  ) : (
                    <ChevronRight size={20} color="#94a3b8" />
                  )}
                </div>
              )}
              
              {(!isProgramCourse || openCategory === category) && (
                <div className="category-items" style={{ padding: isProgramCourse ? '12px 0' : '0', background: isProgramCourse ? '#fafafa' : 'transparent', position: 'relative' }}>
                  {/* Vertical line connecting the steps (only for program courses) */}
                  {isProgramCourse && (
                    <div style={{ position: 'absolute', top: '12px', bottom: '24px', left: '39px', width: '2px', background: '#e2e8f0' }}></div>
                  )}
                  
                  {finalGroupedLessons[category].map((item, index, array) => {
                    const isActive = item.originalIndex === activeIndex;
                    
                    return (
                      <div 
                        key={item.originalIndex} 
                        className={`sidebar-lesson-item ${isActive ? "active" : ""}`}
                        onClick={() => setActiveIndex(item.originalIndex)}
                        style={{
                          padding: isProgramCourse ? '12px 16px 12px 30px' : '10px 16px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          cursor: 'pointer',
                          background: isActive && !isProgramCourse ? '#f8fafc' : 'transparent',
                          color: isActive ? '#334155' : '#64748b',
                          fontWeight: isActive ? '600' : (isProgramCourse ? '500' : '400'),
                          position: 'relative',
                          zIndex: 1,
                          borderLeft: isActive && !isProgramCourse ? '4px solid #4f46e5' : '4px solid transparent'
                        }}
                      >
                        {isProgramCourse ? (
                          <div className="lesson-icon" style={{ 
                            width: '20px', 
                            height: '20px', 
                            background: '#fafafa',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: isActive ? '#818cf8' : '#cbd5e1',
                            zIndex: 2
                          }}>
                            <CheckCircle size={20} />
                          </div>
                        ) : (
                          <div className="lesson-icon" style={{ 
                            width: '24px', 
                            height: '24px', 
                            borderRadius: '50%', 
                            background: isActive ? '#e0e7ff' : '#f1f5f9',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: isActive ? '#4f46e5' : '#94a3b8',
                            fontSize: '12px',
                            fontWeight: '600'
                          }}>
                            {item.originalIndex + 1}
                          </div>
                        )}
                        <span className="lesson-title" style={{ color: isActive ? '#4338ca' : '#475569' }}>{item.q}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="course-main">
        <div className="course-main-header">
          <span className="howtos-badge">Basic</span>
          <h1>{activeLesson.q}</h1>
        </div>

        <div className="course-tabs">
          <button 
            className={`course-tab ${activeTab === "learn" ? "active" : ""}`}
            onClick={() => setActiveTab("learn")}
          >
            Learn
          </button>
          <button 
            className={`course-tab ${activeTab === "try" ? "active" : ""}`}
            onClick={() => setActiveTab("try")}
          >
            Try It Yourself
          </button>
          <button 
            className={`course-tab ${activeTab === "practice" ? "active" : ""}`}
            onClick={() => setActiveTab("practice")}
          >
            Practice
          </button>
        </div>

        <div className="course-content">
          {activeTab === "learn" && (
            <div className="content-card">
              <div className="card-header">
                <Code size={20} className="card-header-icon" />
                <h3>Explanation</h3>
              </div>
              
              <div className="card-body">
                {Array.isArray(activeLesson.a) ? (
                  <ul className="explanation-list">
                    {activeLesson.a.map((point, pIdx) => (
                      <li key={pIdx}>
                        <List size={16} className="li-icon" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="explanation-text markdown-body">
                    <ReactMarkdown>{activeLesson.a}</ReactMarkdown>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === "learn" && activeLesson.example && (
            <div className="content-card example-card" style={{ marginTop: '24px' }}>
              <div className="card-header">
                <h3>Example 1</h3>
              </div>
              <div className="card-body">
                <p className="example-title">{activeLesson.example.title}</p>
                <div className="example-section">
                  <h4>Code:</h4>
                  <pre className="code-block">{activeLesson.example.code}</pre>
                </div>
                <div className="example-section">
                  <h4>Expected Output:</h4>
                  <pre className="output-block">{activeLesson.example.output}</pre>
                </div>
              </div>
            </div>
          )}

          {activeTab === "learn" && (
            <div className="lesson-navigation" style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #e2e8f0' }}>
              {activeIndex > 0 ? (
                <button 
                  onClick={() => setActiveIndex(activeIndex - 1)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 16px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    background: 'white',
                    color: '#334155',
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: '14px',
                    transition: 'all 0.2s'
                  }}
                >
                  <ChevronLeft size={18} /> Previous
                </button>
              ) : (
                <div />
              )}
              
              {activeIndex < totalLessons - 1 ? (
                <button 
                  onClick={() => setActiveIndex(activeIndex + 1)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 16px',
                    borderRadius: '8px',
                    border: 'none',
                    background: '#8b5cf6',
                    color: 'white',
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: '14px',
                    transition: 'all 0.2s'
                  }}
                >
                  Next <ChevronRight size={18} />
                </button>
              ) : (
                <div />
              )}
            </div>
          )}

          {activeTab === "practice" && (
            <div className="content-card practice-card">
              <div className="card-header">
                <Edit3 size={20} className="card-header-icon" />
                <h3>Self-Assessment</h3>
              </div>
              <div className="card-body">
                <p className="practice-prompt">How would you answer this question?</p>
                <textarea 
                  className="practice-textarea" 
                  placeholder="Type your answer here..."
                  value={practiceText}
                  onChange={(e) => setPracticeText(e.target.value)}
                  disabled={showAnswer}
                />
                
                {!showAnswer ? (
                  <button 
                    className="practice-submit-btn" 
                    onClick={() => setShowAnswer(true)}
                    disabled={practiceText.trim().length === 0}
                  >
                    <Eye size={18} /> Reveal Correct Answer
                  </button>
                ) : (
                  <div className="practice-feedback">
                    <h4>Official Answer:</h4>
                    <div className="official-answer-box">
                      {Array.isArray(activeLesson.a) ? (
                        <ul className="explanation-list">
                          {activeLesson.a.map((point, pIdx) => (
                            <li key={pIdx}>
                              <List size={16} className="li-icon" />
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p>{activeLesson.a}</p>
                      )}
                    </div>
                    <button className="practice-retry-btn" onClick={() => { setPracticeText(""); setShowAnswer(false); }}>
                      <RotateCcw size={16} /> Try Again
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
          {activeTab === "try" && (
            <div className="sandbox-container">
              <div className="sandbox-editor-pane">
                <div className="sandbox-header" style={{ display: 'flex', gap: '12px' }}>
                  <select 
                    value={selectedLanguage}
                    onChange={(e) => setSelectedLanguage(e.target.value)}
                    className="language-selector"
                    style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                  >
                    <option value="python">Python (main.py)</option>
                    <option value="javascript">JavaScript (index.js)</option>
                    <option value="java">Java (Main.java)</option>
                    <option value="c">C (main.c)</option>
                    <option value="cpp">C++ (main.cpp)</option>
                    <option value="go">Go (main.go)</option>
                    <option value="ruby">Ruby (main.rb)</option>
                    <option value="php">PHP (main.php)</option>
                    <option value="rust">Rust (main.rs)</option>
                    <option value="bash">Bash (script.sh)</option>
                  </select>
                  <button className="sandbox-run-btn" onClick={executeCode} disabled={isExecuting}>
                    {isExecuting ? 'Running...' : 'Run'}
                  </button>
                </div>
                <textarea
                  className="sandbox-editor"
                  value={sandboxCode}
                  onChange={(e) => setSandboxCode(e.target.value)}
                  spellCheck="false"
                />
              </div>
              <div className="sandbox-output-pane">
                <div className="sandbox-header">
                  <span>Output</span>
                  <button className="sandbox-clear-btn" onClick={() => setSandboxOutput('')}>Clear</button>
                </div>
                <pre className="sandbox-output">
                  {sandboxOutput}
                </pre>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
